import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getSearchParamUrl,
  isCollectionPage,
} from 'components/VideoPlayer/Transcription/MediaUtilities';
import { isEmpty, isNil } from 'lodash';
import {
  AdvancedFilterType,
  DatePeriodFilter,
  DurationFilter,
  MediaType,
  SearchField,
} from 'utils/enum';
import { AdvancedSearchParams, CustomDateParams, Insights } from 'utils/models';
import { readLocal } from './pagination.slice';
import { getSearchFieldByInsight } from 'utils/adapter.util';
import { SEARCH_CLEAR, SET_TOKEN } from 'actions/types';
import { isSemanticSearchAllowed } from 'utils/semanticSearchAccess';

const getSearchFieldFromUrl = (): SearchField | null => {
  const layerParam = getSearchParamUrl('layer') as keyof Insights;
  const searchField = getSearchFieldByInsight(layerParam);

  return searchField;
};

const initialIsShowClipsOnly =
  typeof readLocal('isShowClipsOnly') === 'boolean' && !isCollectionPage()
    ? (readLocal('isShowClipsOnly') as boolean)
    : false;

// Tenants listed for semantic search use it for content items, with or
// without text; Clips stays on the normal library. After login the store is
// created before Explore stores the token from the URL, so the mode is read
// again on SET_TOKEN.
const semanticModeFor = (isShowClipsOnly: boolean): boolean =>
  isSemanticSearchAllowed() && !isShowClipsOnly;

const advancedSearchSlice = createSlice({
  name: 'advanced-search',
  initialState: {
    isShowAdvancedFilter: false,
    // isShowClipsOnly: true,
    isShowClipsOnly: initialIsShowClipsOnly,
    isSemanticSearch: semanticModeFor(initialIsShowClipsOnly),
    // Live (unsubmitted) text of the Library Search input, owned by Sort.tsx.
    librarySearchInput: '',
    // Mode of the last successful Library fetch, null before the first one.
    // Switching Clips changes the mode; Library compares against this to send
    // the first fetch in a new mode to page 1.
    fetchedIsSemanticSearch: null as boolean | null,
    searchParams: {
      uploadDate: null as DatePeriodFilter | null,
      customDate: null as {
        startDate: string | Date;
        endDate: string | Date;
      } | null,
      type: null as MediaType | null,
      duration: null as DurationFilter | null,
      speakerNames: [
        // 'Tony Simmons', 'Anton de Weger'
      ] as string[],
      searchValues: [] as string[],
      searchField: getSearchFieldFromUrl() as SearchField | null,
      searchBy: null as string | null,
      matchType: null as string | null,
      languages: [] as string[],
      excludeWords: [] as string[],
    } as AdvancedSearchParams,
  },
  reducers: {
    resetCustomData: (state) => {
      state.searchParams.customDate = null;
    },
    resetAdvancedSearch: (state) => {
      // state.isShowAdvancedFilter = false;
      state.isShowClipsOnly = false;
      state.isSemanticSearch = semanticModeFor(false);
      const newParams = {
        uploadDate: null,
        customDate: null,
        type: null,
        duration: null,
        speakerNames: [],
        searchValues: [],
        searchField: SearchField.KEYWORD,
        searchBy: null,
        matchType: null,
        languages: [],
        excludeWords: [],
      } as any;
      state.searchParams = newParams;
    },
    toggleAdvancedFilter: (
      state,
      { payload }: PayloadAction<boolean | undefined>,
    ) => {
      if (typeof payload === 'boolean') {
        state.isShowAdvancedFilter = payload;
      } else {
        state.isShowAdvancedFilter = !state.isShowAdvancedFilter;
      }
    },
    setSearchParams: (
      state,
      {
        payload,
      }: PayloadAction<{
        type: AdvancedFilterType;
        data: string | object | null;
        isCustomDate?: boolean;
      }>,
    ) => {
      if (isNil(payload)) return;

      // Select / Unselect existed option
      const select = (field: string | null): any =>
        field !== payload.data ? (payload.data as string) : null;

      const clonedParams = { ...state.searchParams };

      if (payload.type === AdvancedFilterType.DATE) {
        if (!payload?.isCustomDate) {
          clonedParams.uploadDate = select(clonedParams.uploadDate);

          clonedParams.customDate = null;
        } else {
          clonedParams.customDate = payload.data as CustomDateParams;
        }
      } else if (payload.type === AdvancedFilterType.TYPE) {
        clonedParams.type = select(clonedParams.type);
      } else if (payload.type === AdvancedFilterType.DURATION) {
        clonedParams.duration = select(clonedParams.duration);
      } else if (payload.type === AdvancedFilterType.SPEAKERS) {
        // CHECK validation
        if (clonedParams.speakerNames.includes(payload.data as string)) return;

        clonedParams.speakerNames.unshift(payload.data as string);
      } else if (payload.type === AdvancedFilterType.CUSTOM) {
        // CHECK validation
        if (clonedParams.searchValues.includes(payload.data as string)) return;

        clonedParams.searchValues.unshift(payload.data as string);
      } else if (payload.type === AdvancedFilterType.TITLE) {
        clonedParams.searchBy = select(clonedParams.searchBy);
      } else if (payload.type === AdvancedFilterType.MATCH_TYPE) {
        clonedParams.matchType = select(clonedParams.matchType);
      } else if (payload.type === AdvancedFilterType.COLLECTION) {
        if (!clonedParams.collection_id.includes(payload.data as string)) {
          clonedParams.collection_id.unshift(payload.data as string);
        }
      }

      state.searchParams = clonedParams;
    },
    removeSearchTextBox: (
      state,
      { payload }: PayloadAction<{ type: AdvancedFilterType; text: string }>,
    ) => {
      if (isNil(payload)) return;

      const clonedParams = { ...state.searchParams };

      if (payload.type === AdvancedFilterType.SPEAKERS) {
        clonedParams.speakerNames = clonedParams.speakerNames.filter(
          (name) => name !== payload.text,
        );
      } else if (payload.type === AdvancedFilterType.CUSTOM) {
        clonedParams.searchValues = clonedParams.searchValues.filter(
          (value) => value !== payload.text,
        );
      } else if (payload.type === AdvancedFilterType.COLLECTION) {
        clonedParams.collection_id = clonedParams.collection_id.filter(
          (id) => id !== payload.text,
        );
      }

      state.searchParams = clonedParams;
    },
    setSearchField: (state, { payload }: PayloadAction<SearchField>) => {
      if (isNil(payload)) return;

      state.searchParams.searchField = payload;
    },
    toggleSearchClipsMode: (state) => {
      state.isShowClipsOnly = !state.isShowClipsOnly;
      state.isSemanticSearch = semanticModeFor(state.isShowClipsOnly);
    },
    setFetchedSearchMode: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchedIsSemanticSearch = payload;
    },
    setLibrarySearchInput: (state, { payload }: PayloadAction<string>) => {
      state.librarySearchInput = payload;
    },
    setSearchValues: (state, { payload }: PayloadAction<string[]>) => {
      if (isEmpty(payload)) return;

      state.searchParams.searchValues = payload;
    },
    setSpeakerNames: (state, { payload }: PayloadAction<string[]>) => {
      if (isEmpty(payload)) return;

      state.searchParams.speakerNames = payload;
    },
    addExcludeWord: (state, { payload }: PayloadAction<string>) => {
      const words = state.searchParams.excludeWords;
      if (isEmpty(payload) || words.includes(payload)) return;

      words.push(payload);
    },
    removeExcludeWord: (state, { payload }: PayloadAction<string>) => {
      state.searchParams.excludeWords = state.searchParams.excludeWords.filter(
        (word) => word !== payload,
      );
    },
    addLanguage: (state, { payload }: PayloadAction<string>) => {
      const languages = state.searchParams.languages;
      if (isEmpty(payload) || languages.includes(payload)) return;

      languages.push(payload);
    },
    removeLanguage: (state, { payload }: PayloadAction<string>) => {
      state.searchParams.languages = state.searchParams.languages.filter(
        (language) => language !== payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SEARCH_CLEAR, (state) => {
      state.librarySearchInput = '';
    });
    builder.addCase(SET_TOKEN, (state) => {
      state.isSemanticSearch = semanticModeFor(state.isShowClipsOnly);
    });
  },
});

export const {
  toggleAdvancedFilter,
  setSearchParams,
  removeSearchTextBox,
  setSearchField,
  toggleSearchClipsMode,
  setFetchedSearchMode,
  setLibrarySearchInput,
  resetAdvancedSearch,
  setSearchValues,
  setSpeakerNames,
  resetCustomData,
  addExcludeWord,
  removeExcludeWord,
  addLanguage,
  removeLanguage,
} = advancedSearchSlice.actions;

export const advancedSearchReducer = advancedSearchSlice.reducer;
